// =============================================================================
// MeuDriver — InstructorGallery (Batch S2)
// Página de gestão da galeria de fotos do instrutor — /instrutor/galeria
// =============================================================================
// Bucket Supabase Storage: 'instructor-gallery' (criar manualmente no dashboard)
// =============================================================================

import { useRef, useState } from 'react'
import {
  ImagePlus,
  Trash2,
  ChevronUp,
  ChevronDown,
  Loader2,
  Image as ImageIcon,
  X,
} from 'lucide-react'
import { toast } from 'sonner'

import { useAuth } from '@/hooks/useAuth'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import {
  useInstructorGallery,
  useAddGalleryPhoto,
  useDeleteGalleryPhoto,
  useReorderGallery,
  type GalleryPhoto,
} from '@/hooks/useInstructorGallery'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

// ─── Hook auxiliar para buscar o instructor_id do usuário logado ─────────────

function useMyInstructorId() {
  const { user } = useAuth()
  return useQuery<string | null>({
    queryKey: ['my-instructor-id', user?.id],
    enabled:  Boolean(user?.id),
    staleTime: Infinity,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('instructors')
        .select('id')
        .eq('profile_id', user!.id)
        .maybeSingle()

      if (error) throw new Error(error.message)
      return data?.id ?? null
    },
  })
}

// ─── PhotoCard ────────────────────────────────────────────────────────────────

interface PhotoCardProps {
  photo: GalleryPhoto
  isFirst: boolean
  isLast: boolean
  onMoveUp: () => void
  onMoveDown: () => void
  onDelete: () => void
  onPreview: () => void
  isDeleting: boolean
}

function PhotoCard({
  photo,
  isFirst,
  isLast,
  onMoveUp,
  onMoveDown,
  onDelete,
  onPreview,
  isDeleting,
}: PhotoCardProps) {
  return (
    <div className="group relative rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      {/* Imagem */}
      <div
        className="relative aspect-square cursor-pointer overflow-hidden bg-gray-100"
        onClick={onPreview}
      >
        {photo.public_url ? (
          <img
            src={photo.public_url}
            alt={photo.caption ?? 'Foto da galeria'}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full items-center justify-center">
            <ImageIcon className="h-10 w-10 text-gray-300" />
          </div>
        )}
      </div>

      {/* Caption */}
      {photo.caption && (
        <div className="px-3 py-2">
          <p className="text-xs text-gray-600 truncate">{photo.caption}</p>
        </div>
      )}

      {/* Controles */}
      <div className="flex items-center justify-between border-t border-gray-100 px-2 py-1.5">
        {/* Reordenar */}
        <div className="flex gap-1">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={isFirst}
            title="Mover para cima"
            className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronUp className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={isLast}
            title="Mover para baixo"
            className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronDown className="h-4 w-4" />
          </button>
        </div>

        {/* Delete */}
        <button
          type="button"
          onClick={onDelete}
          disabled={isDeleting}
          title="Remover foto"
          data-testid={`delete-photo-${photo.id}`}
          className="flex h-7 w-7 items-center justify-center rounded-lg text-red-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50 transition-colors"
        >
          {isDeleting
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : <Trash2 className="h-3.5 w-3.5" />
          }
        </button>
      </div>
    </div>
  )
}

// ─── UploadCard ───────────────────────────────────────────────────────────────

interface UploadCardProps {
  instructorId: string
}

function UploadCard({ instructorId }: UploadCardProps) {
  const fileInputRef           = useRef<HTMLInputElement>(null)
  const [caption, setCaption]  = useState('')
  const [preview, setPreview]  = useState<string | null>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)

  const addPhoto = useAddGalleryPhoto()

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return

    if (file.size > 5 * 1024 * 1024) {
      toast.error('A imagem deve ter no máximo 5 MB.')
      return
    }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Formato inválido. Use JPG, PNG ou WEBP.')
      return
    }

    setSelectedFile(file)
    setPreview(URL.createObjectURL(file))
  }

  function handleClear() {
    setSelectedFile(null)
    setPreview(null)
    setCaption('')
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  async function handleUpload() {
    if (!selectedFile) return

    try {
      await addPhoto.mutateAsync({ file: selectedFile, caption: caption.trim() || undefined, instructorId })
      toast.success('Foto adicionada à galeria!')
      handleClear()
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao enviar foto.'
      toast.error(msg)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm flex items-center gap-2">
          <ImagePlus className="h-4 w-4 text-brand-600" />
          Adicionar nova foto
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Área de seleção */}
        {preview ? (
          <div className="relative rounded-xl overflow-hidden border border-gray-200 bg-gray-50 aspect-video flex items-center justify-center">
            <img
              src={preview}
              alt="Pré-visualização"
              className="max-h-48 object-contain"
            />
            <button
              type="button"
              onClick={handleClear}
              className="absolute top-2 right-2 flex h-7 w-7 items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="w-full rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 py-8 text-center text-sm text-muted-foreground hover:border-brand-400 hover:bg-brand-50 transition-colors"
          >
            <ImagePlus className="h-8 w-8 mx-auto mb-2 text-gray-300" />
            Clique para selecionar uma imagem
            <br />
            <span className="text-xs">JPG, PNG ou WEBP · máx. 5 MB</span>
          </button>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={handleFileChange}
          data-testid="gallery-file-input"
        />

        {selectedFile && (
          <>
            <div className="space-y-1.5">
              <Label htmlFor="caption" className="text-sm">
                Legenda{' '}
                <span className="text-xs text-muted-foreground font-normal">(opcional)</span>
              </Label>
              <Input
                id="caption"
                placeholder="Ex: Meu veículo de aulas..."
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                maxLength={200}
              />
            </div>

            <Button
              onClick={handleUpload}
              disabled={addPhoto.isPending}
              className="w-full gap-2"
              data-testid="upload-photo-btn"
            >
              {addPhoto.isPending
                ? <><Loader2 className="h-4 w-4 animate-spin" />Enviando...</>
                : <><ImagePlus className="h-4 w-4" />Adicionar à galeria</>
              }
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function InstructorGallery() {
  const { data: instructorId, isLoading: loadingId } = useMyInstructorId()
  const { data: photos = [], isLoading: loadingPhotos } = useInstructorGallery(instructorId ?? '')

  const deletePhoto  = useDeleteGalleryPhoto()
  const reorderGallery = useReorderGallery()

  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  async function handleDelete(photo: GalleryPhoto) {
    if (!instructorId) return
    setDeletingId(photo.id)
    try {
      await deletePhoto.mutateAsync({
        photoId:      photo.id,
        storagePath:  photo.storage_path,
        instructorId,
      })
      toast.success('Foto removida.')
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao remover foto.'
      toast.error(msg)
    } finally {
      setDeletingId(null)
    }
  }

  async function movePhoto(index: number, direction: 'up' | 'down') {
    if (!instructorId) return
    const newPhotos = [...photos]
    const swapIndex = direction === 'up' ? index - 1 : index + 1
    ;[newPhotos[index], newPhotos[swapIndex]] = [newPhotos[swapIndex], newPhotos[index]]
    const ids = newPhotos.map((p) => p.id)

    try {
      await reorderGallery.mutateAsync({ photoIds: ids, instructorId })
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao reordenar galeria.'
      toast.error(msg)
    }
  }

  if (loadingId || loadingPhotos) {
    return (
      <div className="space-y-6 max-w-4xl mx-auto">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="aspect-square rounded-xl" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <ImageIcon className="h-6 w-6 text-brand-600" />
          Galeria de fotos
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Adicione fotos do seu veículo, espaço de treinamento e certificados. Visíveis no seu perfil público.
        </p>
      </div>

      {/* Upload */}
      {instructorId && <UploadCard instructorId={instructorId} />}

      {/* Grid de fotos */}
      {photos.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-gray-200 py-16 text-center text-muted-foreground">
          <ImageIcon className="h-12 w-12 mx-auto mb-3 text-gray-200" />
          <p className="text-sm font-medium">Nenhuma foto ainda</p>
          <p className="text-xs mt-1">Adicione fotos acima para enriquecer seu perfil.</p>
        </div>
      ) : (
        <>
          <div>
            <h2 className="text-sm font-semibold text-gray-700 mb-3">
              {photos.length} foto{photos.length !== 1 ? 's' : ''} na galeria
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {photos.map((photo, index) => (
                <PhotoCard
                  key={photo.id}
                  photo={photo}
                  isFirst={index === 0}
                  isLast={index === photos.length - 1}
                  onMoveUp={() => movePhoto(index, 'up')}
                  onMoveDown={() => movePhoto(index, 'down')}
                  onDelete={() => handleDelete(photo)}
                  onPreview={() => setPreviewUrl(photo.public_url ?? null)}
                  isDeleting={deletingId === photo.id}
                />
              ))}
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Use os botões <strong>▲▼</strong> para reordenar as fotos. A primeira foto aparece em destaque no perfil.
          </p>
        </>
      )}

      {/* Preview Dialog */}
      <Dialog open={Boolean(previewUrl)} onOpenChange={() => setPreviewUrl(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Visualizar foto</DialogTitle>
          </DialogHeader>
          {previewUrl && (
            <img
              src={previewUrl}
              alt="Visualização ampliada"
              className="w-full rounded-xl object-contain max-h-[70vh]"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
